/** Shared shape + helpers for GET /v1/sessions/live.
 *
 * Kept out of the component files so both the global banner and the
 * presence board read the same types and the SAME react-query key —
 * sharing the key is what stops two mounted consumers from polling the
 * endpoint twice on every tick.
 */
export type LiveSession = {
  id: string;
  title: string;
  campaign_id: string | null;
  campaign_name: string;
  operator_id: number | null;
  operator_email: string;
  started_at: string | null;
  scheduled_for: string | null;
  beneficiary_name: string;
  field_joined_at: string | null;
};

export type UpcomingSession = {
  id: string;
  title: string;
  campaign_id: string | null;
  campaign_name: string;
  operator_id: number | null;
  operator_email: string;
  scheduled_for: string;
  beneficiary_name: string;
  /** Its slot has passed and nobody has started it. Late, not absent. */
  overdue: boolean;
};

export type LiveResponse = {
  sessions: LiveSession[];
  count: number;
  upcoming?: UpcomingSession[];
  lead_minutes?: number;
};

/** One key for the unfiltered poll, so every consumer shares one request. */
export const LIVE_QUERY_KEY = ["sessions-live", "all"] as const;

/** Whole minutes until `iso`, floored at 0. Rounded UP: "in 1 minute"
 *  with 70 seconds left is closer to the truth than "in 1 minute" with
 *  10 seconds left, and an operator who reads "2" and arrives to find it
 *  already started will not trust the next one. */
export function minutesUntil(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return Math.ceil(ms / 60_000);
}
